// Verificación de seguridad de Cloudflare Turnstile en el inicio de sesión. Supabase Auth valida el
// token en el servidor ([auth.captcha] en supabase/config.toml; en producción, el panel de Supabase),
// así que un programa no puede probar contraseñas llamando directo a la API.
//
// Cada token sirve para un solo intento: después de cada intento hay que pedir uno nuevo (reset).
// Sin VITE_TURNSTILE_SITE_KEY la verificación se omite (solo sirve si Auth tampoco la exige).
import { useCallback, useEffect, useRef, type RefObject } from 'react';

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const TOKEN_TIMEOUT_MS = 20_000;

export const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '';

interface TurnstileOptions {
  sitekey: string;
  action?: string;
  language?: string;
  theme?: 'auto' | 'light' | 'dark';
  size?: 'normal' | 'flexible' | 'compact';
  appearance?: 'always' | 'execute' | 'interaction-only';
  callback?: (token: string) => void;
  'expired-callback'?: () => void;
  'error-callback'?: () => void;
}

interface TurnstileApi {
  render: (container: HTMLElement, options: TurnstileOptions) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let loading: Promise<TurnstileApi> | null = null;

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  loading ??= new Promise<TurnstileApi>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_URL;
    script.async = true;
    script.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error('turnstile')));
    script.onerror = () => {
      loading = null;
      script.remove();
      reject(new Error('turnstile'));
    };
    document.head.appendChild(script);
  });
  return loading;
}

export class CaptchaUnavailable extends Error {}

export interface Captcha {
  /** Token para el próximo intento; espera a que la verificación termine. null si no hay verificación. */
  getToken: () => Promise<string | null>;
  /** Descarta el token usado y pide uno nuevo. */
  reset: () => void;
}

/** Muestra la verificación dentro de `container` (solo aparece si Cloudflare pide interacción). */
export function useTurnstile(container: RefObject<HTMLElement | null>): Captcha {
  const widget = useRef<{ api: TurnstileApi; id: string } | null>(null);
  const token = useRef<string | null>(null);
  const failed = useRef(false);
  const waiting = useRef<((value: string | null) => void)[]>([]);

  const settle = useCallback((value: string | null) => {
    const resolvers = waiting.current;
    waiting.current = [];
    for (const resolve of resolvers) resolve(value);
  }, []);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || !container.current) return;
    const element = container.current;
    let cancelled = false;
    loadTurnstile()
      .then((api) => {
        if (cancelled) return;
        const id = api.render(element, {
          sitekey: TURNSTILE_SITE_KEY,
          action: 'login',
          language: 'es',
          theme: 'auto',
          size: 'flexible',
          appearance: 'interaction-only',
          callback: (value) => {
            token.current = value;
            failed.current = false;
            settle(value);
          },
          'expired-callback': () => {
            token.current = null;
          },
          'error-callback': () => {
            token.current = null;
            failed.current = true;
            settle(null);
          },
        });
        widget.current = { api, id };
      })
      .catch(() => {
        failed.current = true;
        settle(null);
      });
    return () => {
      cancelled = true;
      if (widget.current) widget.current.api.remove(widget.current.id);
      widget.current = null;
    };
  }, [container, settle]);

  const getToken = useCallback(async () => {
    if (!TURNSTILE_SITE_KEY) return null;
    if (token.current) return token.current;
    if (failed.current) {
      // Sin el script de Cloudflare no hay nada que esperar. Si el widget falló, se intenta de nuevo.
      if (!widget.current) throw new CaptchaUnavailable();
      failed.current = false;
      widget.current.api.reset(widget.current.id);
    }
    const value = await new Promise<string | null>((resolve) => {
      waiting.current.push(resolve);
      setTimeout(() => resolve(null), TOKEN_TIMEOUT_MS);
    });
    if (!value) throw new CaptchaUnavailable();
    return value;
  }, []);

  const reset = useCallback(() => {
    token.current = null;
    if (widget.current) widget.current.api.reset(widget.current.id);
  }, []);

  return { getToken, reset };
}
